import { z } from "zod";

import { apiClient } from "./api";

const timestampSchema = z.string().datetime({ offset: true });

export const knowledgeRepositorySchema = z
  .object({
    id: z.guid(),
    slug: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    is_default: z.boolean(),
    archived_at: timestampSchema.nullable(),
    created_by: z.guid(),
    created_at: timestampSchema,
    updated_at: timestampSchema,
  })
  .strict();
export type KnowledgeRepository = z.infer<typeof knowledgeRepositorySchema>;

const graphReadinessSchema = z
  .object({
    ready: z.boolean(),
    unscoped_nodes: z.number().int().nonnegative(),
    unscoped_relationships: z.number().int().nonnegative(),
    cross_repository_relationships: z.number().int().nonnegative(),
    duplicate_document_identities: z.number().int().nonnegative(),
    duplicate_entity_identities: z.number().int().nonnegative(),
  })
  .strict();

export const knowledgeRepositoryReadinessSchema = z
  .object({
    multi_kr_enabled: z.boolean(),
    relational_backfill_complete: z.boolean(),
    relational_ownerless_records: z.number().int().nonnegative(),
    relational_ownership_mismatches: z.number().int().nonnegative(),
    relational_cohort_mismatches: z.number().int().nonnegative(),
    relational_error: z.string().nullable(),
    graph_configured: z.boolean(),
    graph: graphReadinessSchema.nullable(),
    graph_error: z.string().nullable(),
    readiness_checks_passed: z.boolean(),
    creation_enabled: z.boolean(),
  })
  .strict();
export type KnowledgeRepositoryReadiness = z.infer<typeof knowledgeRepositoryReadinessSchema>;

export const knowledgeRepositoryAuditSchema = z
  .object({
    id: z.guid(),
    kr_id: z.guid(),
    action: z.string(),
    actor_id: z.guid(),
    details: z.record(z.string(), z.string().nullable()),
    created_at: timestampSchema,
  })
  .strict();
export type KnowledgeRepositoryAudit = z.infer<typeof knowledgeRepositoryAuditSchema>;

const repositoryListSchema = z.array(knowledgeRepositorySchema);

export interface CreateKnowledgeRepositoryInput {
  slug: string;
  name: string;
  description: string | null;
}

export interface UpdateKnowledgeRepositoryInput {
  name?: string;
  description?: string | null;
}

export const KNOWLEDGE_REPOSITORIES_QUERY_KEY = ["knowledge-repositories"] as const;

export const knowledgeRepositoriesApi = {
  list: (includeArchived = false) =>
    apiClient.get("/admin/knowledge-repositories", repositoryListSchema, {
      query: { include_archived: includeArchived },
    }),
  readiness: () =>
    apiClient.get("/admin/knowledge-repositories/readiness", knowledgeRepositoryReadinessSchema),
  create: (input: CreateKnowledgeRepositoryInput) =>
    apiClient.post("/admin/knowledge-repositories", knowledgeRepositorySchema, input),
  update: (id: string, input: UpdateKnowledgeRepositoryInput) =>
    apiClient.patch(`/admin/knowledge-repositories/${id}`, knowledgeRepositorySchema, input),
  archive: (id: string) =>
    apiClient.post(`/admin/knowledge-repositories/${id}/archive`, knowledgeRepositorySchema, {}),
  reactivate: (id: string) =>
    apiClient.post(`/admin/knowledge-repositories/${id}/reactivate`, knowledgeRepositorySchema, {}),
  audit: (id: string, limit = 100) =>
    apiClient.get(
      `/admin/knowledge-repositories/${id}/audit`,
      z.array(knowledgeRepositoryAuditSchema),
      { query: { limit } },
    ),
};
